import { FC, PropsWithChildren, createContext, useContext } from "react";
import type { User } from "../api/bsso";

export type AuthContext = {
  user: User | null | undefined;
  signin(): void;
  signout(): void;
};

const AuthContext = createContext<AuthContext>({
  user: undefined,
  signin: () => {
    /* no-op */
  },
  signout: () => {
    /* no-op */
  },
});

export declare namespace useAuth {
  export interface Required {
    redirectPath?: string;
  }
  export interface Options {
    required?: Required | boolean;
  }
}

function parseUseAuthOptions(
  requireAuthOrOptions?: useAuth.Options | boolean,
): useAuth.Options {
  if (!requireAuthOrOptions) {
    return { required: false };
  }
  if (typeof requireAuthOrOptions === "boolean") {
    return { required: true };
  }
  return requireAuthOrOptions;
}

export function useAuth(
  requireAuthOrOptions?: useAuth.Options | boolean,
): AuthContext {
  const { required } = parseUseAuthOptions(requireAuthOrOptions);
  const ctx = useContext(AuthContext);
  if (required) {
    if (ctx.user == null) {
      ctx.signin();
    }
  }
  return ctx;
}

function useProvideAuth(user: User | null | undefined) {
  return {
    user,
    signin() {
      // Redirect to sign-in page with the intended redirect URL
      const currentPath = location.pathname + location.search + location.hash;
      const redirectParam = encodeURIComponent(currentPath);
      location.href = `/signin?redirect=${redirectParam}`;
    },
    signout() {
      location.href = "/bsso/logout";
    },
  };
}

export declare namespace AuthProvider {
  export interface Props {
    user: User | null | undefined;
  }
}

export const AuthProvider: FC<PropsWithChildren<AuthProvider.Props>> = ({
  children,
  user,
}) => {
  const auth = useProvideAuth(user);
  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};
